import React from "react";
import { Seeker } from "../../types/seeker";
import { type StoredMatch, type StoredMatchStatus } from "../../data/matchingService";
import {
    ScheduledSession,
    formatSessionDate,
    formatSessionTime,
} from "../../data/sessionService";

interface SeekerSidebarProps {
    seeker: Seeker;
    sessions: ScheduledSession[];
    onRefresh: () => void;
    match?: StoredMatch | null;
    loadingMatch?: boolean;
    matchError?: string | null;
    onOpenGuide?: () => void;
    onOpenManualMatch?: () => void;
    locationLabel?: string | null;
    pastMatches?: StoredMatch[] | null;
    loadingPastMatches?: boolean;
    pastMatchesError?: string | null;
    seekerInitiatedChanges?: number;
    guideInitiatedChanges?: number;
    onResetSeekerInitiatedChanges?: () => void;
    onResetGuideInitiatedChanges?: () => void;
}

const badgeClasses =
    "inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em]";

const cardClasses =
    "rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-[inset_0_0_0_1px_rgba(44,39,49,0.05)]";

const sectionTitleClasses =
    "text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]";

function formatHoursFromMinutes(minutes: number): string {
    if (!minutes || minutes <= 0) return "No time logged";

    const hours = minutes / 60;
    if (hours >= 1) {
        const rounded = hours >= 10 ? Math.round(hours) : Number(hours.toFixed(1));
        return `${rounded}${rounded === 1 ? " hour" : " hours"}`;
    }

    return `${minutes} minutes`;
}

function formatCurrency(value: number | null | undefined): string {
    if (!value || value <= 0) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

function getLatestSession(sessions: ScheduledSession[]): ScheduledSession | null {
    if (!sessions.length) return null;

    return (
        sessions.reduce<
            | {
                  session: ScheduledSession;
                  timestamp: number;
              }
            | null
        >((latest, session) => {
            const seed = session.session_date
                ? `${session.session_date}T${session.session_time ?? "00:00:00"}`
                : session.created_at;
            if (!seed) return latest;

            const timestamp = new Date(seed).getTime();
            if (Number.isNaN(timestamp)) return latest;

            if (!latest || timestamp > latest.timestamp) {
                return { session, timestamp };
            }

            return latest;
        }, null)?.session ?? null
    );
}

function determineLastContact(sessions: ScheduledSession[], match?: StoredMatch | null): string {
    const latestSession = getLatestSession(sessions);
    if (latestSession) {
        const hasDate = Boolean(latestSession.session_date);
        const hasTime = Boolean(latestSession.session_time);
        const dateLabel = hasDate ? formatSessionDate(latestSession.session_date!) : null;
        const timeLabel = hasTime ? formatSessionTime(latestSession.session_time!) : null;

        if (dateLabel && timeLabel) return `${dateLabel} at ${timeLabel}`;
        if (dateLabel) return dateLabel;
        if (timeLabel) return timeLabel;
        if (latestSession.created_at) return new Date(latestSession.created_at).toLocaleString();
    }

    if (match?.created_at) {
        return new Date(match.created_at).toLocaleDateString();
    }

    return "No interactions yet";
}

function formatDateLabel(value?: string | null): string {
    if (!value) return "Unknown date";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
}

function formatScoreLabel(score?: number | null): string | null {
    if (score == null) return null;
    const normalized = score > 1 ? score : score * 100;
    if (!Number.isFinite(normalized)) return null;
    return `${Math.round(normalized)}% match`;
}

function getMatchStatusTheme(status?: StoredMatchStatus | null) {
    switch (status) {
        case "invalidated":
            return { label: "Invalidated", className: "bg-[#B24E4E] text-[#FEF6D6]" };
        case "expired":
            return { label: "Expired", className: "bg-[#8C7A5B] text-[#FEF6D6]" };
        case "active":
            return { label: "Active", className: "bg-[#2C2731] text-[#FEF6D6]" };
        default:
            return { label: "Unknown", className: "bg-[#403851] text-[#FEF6D6]" };
    }
}

function formatSourceLabel(source?: string | null): string {
    if (!source) return "Unknown source";
    // Convert snake_case to Title Case
    return source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function formatReasonLabel(reasonLabel?: string | null, reasonId?: string | null): string {
    const label = reasonLabel?.trim();
    if (label && label.length > 0) {
        return label;
    }

    const fallback = reasonId?.replace(/_/g, " ").trim();
    if (fallback && fallback.length > 0) {
        return fallback.replace(/\b\w/g, (char) => char.toUpperCase());
    }

    return "Reason not recorded";
}

export default function SeekerSidebar({
    seeker,
    sessions,
    onRefresh,
    match,
    loadingMatch,
    matchError,
    onOpenGuide,
    onOpenManualMatch,
    locationLabel,
    pastMatches,
    loadingPastMatches,
    pastMatchesError,
    seekerInitiatedChanges,
    guideInitiatedChanges,
    onResetSeekerInitiatedChanges,
    onResetGuideInitiatedChanges,
}: SeekerSidebarProps) {
    const totalSessions = sessions.length;
    const completedSessions = sessions.filter((session) => session.session_status === "completed").length;
    const totalMinutes = sessions.reduce((sum, session) => sum + (session.duration_minutes ?? 0), 0);
    const connectionDate = match?.created_at
        ? new Date(match.created_at).toLocaleDateString()
        : new Date(seeker.joinedAt).toLocaleDateString();
    const lastContactLabel = determineLastContact(sessions, match);

    const connectionStats = [
        {
            label: "Connection Date",
            value: connectionDate,
        },
        {
            label: "Total Sessions",
            value: totalSessions ? `${totalSessions} (${completedSessions} completed)` : "No sessions yet",
        },
        {
            label: "Total Hours",
            value: formatHoursFromMinutes(totalMinutes),
        },
        {
            label: "Total Paid",
            value: formatCurrency(seeker.lifetimeValue),
        },
        {
            label: "Last Contact",
            value: lastContactLabel,
            fullWidth: true,
        },
    ];

    const guideInitial =
        match?.guide_details?.full_name?.trim()?.charAt(0).toUpperCase() ??
        match?.guide_details?.firstName?.trim()?.charAt(0).toUpperCase() ??
        "G";

    const guideAvatarUrl =
        match?.guide_details?.avatarUrl ??
        match?.guide_details?.guides_enhanced_profile?.image_url ??
        null;

    const rawYearsExperienceText = match?.guide_details?.yearsExperienceText ?? null;
    const numericYearsExperience = match?.guide_details?.yearsExperience ?? null;
    let yearsExperienceLabel: string | null = null;

    if (rawYearsExperienceText && rawYearsExperienceText.trim().length > 0) {
        const trimmed = rawYearsExperienceText.trim();
        const hasYearWord = /year/i.test(trimmed);
        yearsExperienceLabel = hasYearWord ? trimmed : `${trimmed} years of experience`;
    } else if (typeof numericYearsExperience === "number" && !Number.isNaN(numericYearsExperience)) {
        const isInteger = Number.isInteger(numericYearsExperience);
        const formattedValue = isInteger ? numericYearsExperience : Number(numericYearsExperience.toFixed(1));
        const unitLabel = formattedValue === 1 ? "year" : "years";
        yearsExperienceLabel = `${formattedValue} ${unitLabel} of experience`;
    }

    const guideLocation = match?.guide_details?.location?.trim() || null;
    const guideEmail = match?.guide_details?.email?.trim() || null;
    const guidePhone = match?.guide_details?.phone?.trim() || null;
    const guideModalities =
        match?.guide_details?.experience_types
            ?.map((type) => type?.trim())
            .filter((type): type is string => Boolean(type)) ?? [];
    const guideSupportsRaw =
        match?.guide_details?.support_types
            ?.map((type) => type?.trim())
            .filter((type): type is string => Boolean(type)) ?? [];
    // Split on any dash variant (em dash, en dash, hyphen) and take first part, then deduplicate case-insensitively
    const guideSupports = guideSupportsRaw
        .map((type) => {
            // Match space + any dash character + space
            const dashMatch = type.match(/^(.+?)\s+[—–-]\s+/);
            if (dashMatch) {
                return dashMatch[1].trim();
            }
            return type.trim();
        })
        .filter((value, index, self) =>
            self.findIndex((v) => v.toLowerCase() === value.toLowerCase()) === index
        );
    const guideModalitiesLabel = guideModalities.length ? guideModalities.join(", ") : null;
    const guideSupportsLabel = guideSupports.length ? guideSupports.join(", ") : null;
    const guideInfoItems = [
        { label: "Location", value: guideLocation },
        { label: "Modalities", value: guideModalitiesLabel },
        { label: "Supports", value: guideSupportsLabel },
        { label: "Email", value: guideEmail },
        { label: "Phone", value: guidePhone },
    ].filter((item): item is { label: string; value: string } => Boolean(item.value));
    const rawScore = match?.match_score ?? null;
    const normalizedScore =
        rawScore == null ? null : rawScore > 1 ? rawScore : rawScore * 100;
    const scorePercentage = normalizedScore != null ? `${Math.round(normalizedScore)}% match` : null;
    const inactiveMatches = (pastMatches ?? []).filter(
        (pastMatch) => pastMatch?.status && pastMatch.status !== "active"
    );
    const hasInactiveMatches = inactiveMatches.length > 0;

    return (
        <div className="flex w-full max-w-xs flex-col gap-6">
            <div className={`${cardClasses} flex flex-col gap-5`}>
                <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-[#2C2731]">Connected Guide</h3>
                    {scorePercentage && <span className={`${badgeClasses} bg-[#2C2731] text-[#FEF6D6]`}>{scorePercentage}</span>}
                </div>

                <div className="flex flex-col items-center gap-4 text-center">
                    {loadingMatch && <p className="text-sm text-[#403851]">Loading guide…</p>}
                    {matchError && <p className="text-sm text-[#B24E4E]">{matchError}</p>}

                    {!loadingMatch && !matchError && !match && (
                        <div className="flex flex-col items-center gap-3">
                            <p className="text-sm text-[#403851]">No guide matched yet</p>
                            {onOpenManualMatch && (
                                <button
                                    className="w-full rounded-xl border border-[#2C2731] bg-[#2C2731] px-3 py-2 text-sm font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                                    onClick={onOpenManualMatch}
                                >
                                    Manual Match
                                </button>
                            )}
                        </div>
                    )}

                    {!loadingMatch && !matchError && match && (
                        <>
                            <div className="flex flex-col items-center gap-3">
                                <div className="relative h-20 w-20 overflow-hidden rounded-full border border-[#2C2731]/10 bg-[#FFFCEF]">
                                    {guideAvatarUrl ? (
                                        <img
                                            src={guideAvatarUrl}
                                            alt={`${match.guide_details?.full_name ?? "Guide"} profile`}
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-3xl font-semibold text-[#2C2731]">
                                            {guideInitial}
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <div className="text-base font-semibold text-[#2C2731]">
                                        {match.guide_details?.full_name ?? "Guide"}
                                    </div>
                                    {guideSupportsLabel && (
                                        <div className="text-xs font-medium uppercase tracking-[0.1em] text-[#403851]">
                                            {guideSupportsLabel}
                                        </div>
                                    )}
                                    {yearsExperienceLabel && (
                                        <div className="text-xs text-[#403851]/90">{yearsExperienceLabel}</div>
                                    )}
                                </div>
                            </div>

                            {guideInfoItems.length > 0 && false && (
                                <div className="grid w-full gap-2 text-left text-sm text-[#2C2731] sm:grid-cols-2">
                                    {guideInfoItems.map((item) => (
                                        <div
                                            key={item.label}
                                            className="rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3"
                                        >
                                            <div className="text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-[#403851]/80">
                                                {item.label}
                                            </div>
                                            <div className="mt-1 break-words text-sm font-semibold text-[#2C2731]">
                                                {item.value}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                className="w-full rounded-xl border border-[#2C2731] bg-[#2C2731] px-3 py-2 text-sm font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                                onClick={onOpenGuide}
                            >
                                View Guide Profile
                            </button>
                            {onOpenManualMatch && (
                                <button
                                    className="w-full rounded-xl border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm font-semibold text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                                    onClick={onOpenManualMatch}
                                >
                                    Change Guide
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className={cardClasses}>
                <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-[#2C2731]">Connection Snapshot</h3>
                    {/*locationLabel && <span className={`${badgeClasses} bg-[#403851] text-[#FEF6D6]`}>{locationLabel}</span>*/}
                </div>
                <div className="mt-4 grid gap-4">
                    {connectionStats.map((stat) => (
                        <div
                            key={stat.label}
                            className={[
                                "rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3",
                                stat.fullWidth ? "col-span-full" : "",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                        >
                            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#403851]">
                                {stat.label}
                            </div>
                            <div className="mt-1 text-sm font-semibold text-[#2C2731]">{stat.value}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={cardClasses}>
                <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-[#2C2731]">Recent Match Outcomes</h3>
                    {loadingPastMatches && <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#403851]/70">Loading…</span>}
                </div>
                <div className="mt-4 flex flex-col gap-3">
                    {pastMatchesError && (
                        <div className="rounded-xl border border-[#B24E4E]/40 bg-[#FCEDEA] px-4 py-3 text-sm text-[#B24E4E]">
                            {pastMatchesError}
                        </div>
                    )}

                    {!pastMatchesError && hasInactiveMatches && inactiveMatches.map((pastMatch) => {
                        const statusTheme = getMatchStatusTheme(pastMatch.status);
                        const exclusion = pastMatch.exclusion ?? null;
                        const reasonLabel = formatReasonLabel(null, exclusion?.reason_id);
                        const sourceLabel = formatSourceLabel(exclusion?.source);
                        const exclusionDate = exclusion?.created_at ? formatDateLabel(exclusion.created_at) : null;
                        const endedDate = !exclusionDate && pastMatch.ended_at ? formatDateLabel(pastMatch.ended_at) : null;
                        const note = exclusion?.note?.trim();

                        return (
                            <div
                                key={pastMatch.id}
                                className="rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-semibold text-[#2C2731]">{reasonLabel}</div>
                                        <div className="text-xs text-[#403851]">
                                            Source: {sourceLabel}
                                            {exclusionDate ? ` • Excluded ${exclusionDate}` : endedDate ? ` • Ended ${endedDate}` : ""}
                                        </div>
                                    </div>
                                    <span className={`${badgeClasses} ${statusTheme.className}`}>
                                        {statusTheme.label}
                                    </span>
                                </div>
                                {note && (
                                    <div className="mt-2 text-xs text-[#403851]/90">{note}</div>
                                )}
                                {!note && !exclusion && (
                                    <div className="mt-2 text-xs text-[#403851]/70">
                                        No exclusion details were recorded for this match.
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {!pastMatchesError && !loadingPastMatches && !hasInactiveMatches && (
                        <p className="text-sm text-[#403851]">No invalidated matches recorded for this seeker.</p>
                    )}
                </div>
            </div>

            <div className={cardClasses}>
                <h3 className="text-lg font-semibold text-[#2C2731]">Match Change Counters</h3>
                <div className="mt-4 space-y-3">
                    <div className="rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#403851]">
                                    Seeker Initiated
                                </div>
                                <div className="mt-1 text-lg font-semibold text-[#2C2731]">
                                    {seekerInitiatedChanges ?? 0}
                                </div>
                            </div>
                            {onResetSeekerInitiatedChanges && seekerInitiatedChanges !== undefined && seekerInitiatedChanges > 0 && (
                                <button
                                    onClick={onResetSeekerInitiatedChanges}
                                    className="rounded-lg border border-[#2C2731] px-3 py-1.5 text-xs font-semibold text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                                >
                                    Reset
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#403851]">
                                    Guide Initiated
                                </div>
                                <div className="mt-1 text-lg font-semibold text-[#2C2731]">
                                    {guideInitiatedChanges ?? 0}
                                </div>
                            </div>
                            {onResetGuideInitiatedChanges && guideInitiatedChanges !== undefined && guideInitiatedChanges > 0 && (
                                <button
                                    onClick={onResetGuideInitiatedChanges}
                                    className="rounded-lg border border-[#2C2731] px-3 py-1.5 text-xs font-semibold text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                                >
                                    Reset
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/*<div className={cardClasses}>
                <h3 className="text-lg font-semibold text-[#2C2731]">Quick Actions</h3>
                <div className="mt-4 space-y-3">
                    <button className="flex w-full items-center gap-3 rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3 text-left text-sm font-semibold text-[#2C2731] transition-colors hover:bg-[#F2EAC3]">
                        <img src="/message.svg" alt="Send Message" className="h-4 w-4" />
                        Send Message
                    </button>
                    <button className="flex w-full items-center gap-3 rounded-xl border border-[#B24E4E]/40 bg-[#FCEDEA] px-4 py-3 text-left text-sm font-semibold text-[#B24E4E] transition-colors hover:border-[#B24E4E] hover:bg-[#FAD8D8]">
                        <img src="/suspend.svg" alt="Suspend Account" className="h-4 w-4" />
                        Suspend Account
                    </button>
                </div>
            </div>*/}
        </div>
    );
}
